import {
  Badge,
  Button,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import type { SearchFilterConditionCustomAttribute } from 'lib-search-types';
import { SearchFilterLayout } from 'lib-search-ui';
import orderBy from 'lodash/orderBy';
import {
  ChangeEvent,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';

import {
  CustomFieldAttribute,
  CustomFieldEntity,
  customFieldsApi,
  CustomFieldSelectAttribute,
  CustomFieldTextAttribute,
} from 'apis/customFields';

import { SearchCustomAttributesFilterSelect } from './components/select/searchCustomAttributesFilterSelect';
import { SearchCustomAttributesFilterText } from './components/text/searchCustomAttributesFilterText';
import styles from './searchCustomAttributesFilter.module.scss';
import { SearchCustomAttributesFilterProps } from './searchCustomAttributesFilter.types';

/**
 * A filter component for custom attributes (contact or conversation).
 *
 * Displays a searchable list of attributes, with sub-views for select (tree) and text (existence
 * operators) attribute types.
 */
const SearchCustomAttributesFilterInner = <
  TEntity extends CustomFieldEntity = CustomFieldEntity,
>({
  entityId,
  conditions = [],
  onChange,
  buttonProps,
  popoverContentProps,
  children,
}: SearchCustomAttributesFilterProps<TEntity>): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeAttribute, setActiveAttribute] =
    useState<CustomFieldAttribute | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const [readAttributes, { attributes, isLoading, isUninitialized }] =
    customFieldsApi.useLazyReadAttributesQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        attributes: data?.data.attributes ?? [],
      }),
    });

  // Fetch attributes the first time the dropdown opens.
  useUpdateEffect(() => {
    if (isOpen && isUninitialized) {
      const preferCacheValue = true;
      readAttributes({ entityId }, preferCacheValue);
    }
  }, [isOpen, isUninitialized, readAttributes, entityId]);

  const handleOpenChange = useCallback((newIsOpen: boolean) => {
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      setTimeout(() => inputRef.current?.focus());
    } else {
      setActiveAttribute(null);
      setSearchTerm('');
    }
  }, []);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    inputRef.current?.focus();
  }, []);

  const handleSelectAttribute = useCallback(
    ({ selectedItem }: SelectionListChangeEvent<CustomFieldAttribute>) => {
      setActiveAttribute(selectedItem.value);
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus());
    },
    [],
  );

  const handleBack = useCallback(() => {
    setActiveAttribute(null);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.focus());
  }, []);

  /** Get the condition for a specific attribute by its ID. */
  const getConditionForAttribute = useCallback(
    (attributeId: string) =>
      conditions.find(
        (c) => 'attributeId' in c && c.attributeId === attributeId,
      ),
    [conditions],
  );

  const visibleAttributes = useMemo(() => {
    const fuse = new Fuse<CustomFieldAttribute>(attributes, {
      keys: ['label', 'identifier'],
      threshold: 0.2,
      distance: 200,
    });

    const filtered = debouncedSearchTerm
      ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
      : [...attributes];

    return orderBy<CustomFieldAttribute>(filtered, 'is_deactivated', 'asc');
  }, [attributes, debouncedSearchTerm]);

  const field = `${entityId}_custom_attribute` as const;

  /** Handles rendering the list of custom attributes. */
  const renderAttributesList = () => {
    return (
      <>
        <SearchFilterLayout.Row borderBottom>
          <InputText
            ref={inputRef}
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search attributes..."
            suffix={{
              type: 'custom',
              value: (
                <FlexBox alignItems="center" padding="0 100">
                  <IconButton
                    variant="tertiary"
                    icon={searchTerm ? 'close' : 'search'}
                    onClick={searchTerm ? handleClearSearch : undefined}
                  />
                </FlexBox>
              ),
              noWrap: true,
            }}
          />
        </SearchFilterLayout.Row>

        <SearchFilterLayout.Row ref={listWrapperRef} noPadding scrollable>
          <SelectionList
            isActive={isOpen}
            selectedValues={activeAttribute ? [activeAttribute] : undefined}
            onChange={handleSelectAttribute}
            equalityFn={(a, b) => a.identifier === b.identifier}
            defaultActiveItemIndex={0}
          >
            {visibleAttributes.map((attribute, index, list) => {
              const isFirstDeactivated =
                attribute.is_deactivated &&
                (index === 0 || !list[index - 1].is_deactivated);

              const attrCondition = getConditionForAttribute(attribute.id);
              const count = attrCondition
                ? attrCondition.values.length || 1
                : undefined;

              return (
                <Fragment key={attribute.identifier}>
                  {isFirstDeactivated && (
                    <SelectionList.Divider label="Deactivated" />
                  )}

                  <SearchFilterLayout.Item
                    value={attribute}
                    label={attribute.label}
                    highlight={debouncedSearchTerm}
                    noFixedSelectionStateSpace
                    contentClassName={classNames(styles.itemContent, {
                      [styles.itemContentWithBadge]: !!count,
                    })}
                    textProps={{
                      color: attribute.is_deactivated ? 'tertiary' : undefined,
                    }}
                  >
                    {count && (
                      <Badge variant="secondary" size="small">
                        {count}
                      </Badge>
                    )}

                    <Icon name="angle-right" />
                  </SearchFilterLayout.Item>
                </Fragment>
              );
            })}

            {isLoading && <SearchFilterLayout.Loading />}

            {!isLoading && visibleAttributes.length === 0 && (
              <SearchFilterLayout.Empty />
            )}
          </SelectionList>
        </SearchFilterLayout.Row>
      </>
    );
  };

  /** Handles rendering of the active attribute according to its type. */
  const renderAttributeType = () => {
    if (!activeAttribute) return null;

    type SubComponentOnChange = (
      condition: SearchFilterConditionCustomAttribute | null,
      attributeId: string,
    ) => void;

    return activeAttribute.type === 'select' ? (
      <SearchCustomAttributesFilterSelect
        field={field}
        attribute={activeAttribute as CustomFieldSelectAttribute}
        condition={getConditionForAttribute(activeAttribute.id)}
        onChange={onChange as SubComponentOnChange}
        onBack={handleBack}
      />
    ) : (
      <SearchCustomAttributesFilterText
        field={field}
        attribute={activeAttribute as CustomFieldTextAttribute}
        condition={getConditionForAttribute(activeAttribute.id)}
        onChange={onChange as SubComponentOnChange}
        onBack={handleBack}
      />
    );
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <Button
          variant="tertiary"
          {...buttonProps}
          label={buttonProps?.label ?? (conditions.length || undefined)}
          slotEnd={
            buttonProps?.slotEnd ?? (
              <Icon name={isOpen ? 'angle-up' : 'angle-down'} />
            )
          }
        >
          {children}
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.popoverContent}
          side="bottom"
          align="start"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          {...popoverContentProps}
        >
          <SearchFilterLayout>
            {activeAttribute ? renderAttributeType() : renderAttributesList()}
          </SearchFilterLayout>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export const SearchCustomAttributesFilter = memo(
  SearchCustomAttributesFilterInner,
) as unknown as typeof SearchCustomAttributesFilterInner;
